import React, { useState } from 'react';
import { Box, Button, Divider, Tooltip, Typography, Grid } from '@mui/material';
import { TRANSPARENT_BLACK_50, WHITE } from 'utilities/colors';
import { UPLOAD_ANNOTATIONS_ERROR_MESSAGE } from 'components/ImageViewer/constants';
import { CheckOutlined as CheckOutlinedIcon } from '@mui/icons-material';
import { ANNOTATION_PROVIDERS } from 'components/OrderSlidesList/ExtAnnotations/constants';
import { DefaultModal } from 'components/Modals/DefaultModal';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { uploadExtAnnotations } from 'components/OrderSlidesList/ExtAnnotations/utils';
import { FilesList } from 'components/OrderSlidesList/ExtAnnotations/FilesList';
import { AnnotationsDropZone } from 'components/OrderSlidesList/ExtAnnotations/DropZone';

export const UploadAnnotationsViewModal = ({
  onUpload,
  classes,
  open,
  onClose,
  isBatch,
}) => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [preparedForUpload, setPreparedForUpload] = useState([]);
  const { showError } = useSnackbar();
  const getBubbleClassName = (name) =>
    selectedProvider === name
      ? classes.providerButtonSelected
      : classes.providerButtonSelectedHidden;

  const getProviderClassName = (name) =>
    selectedProvider === name
      ? `${classes.dataProvider} ${classes.dataProviderSelected}`
      : classes.dataProvider;

  const onAddToUpload = (files) => {
    uploadExtAnnotations({
      selectedProvider,
      files,
      setPreparedForUpload,
    });
  };

  const onDeleteFromUpload = (name, provider) => {
    setPreparedForUpload((prev) =>
      prev.filter(
        ({ fileName, type }) => !(fileName === name && type === provider),
      ),
    );
  };

  const handleUpload = () => {
    onUpload(preparedForUpload)
      .then(() => {
        onClose();
        setPreparedForUpload([]);
      })
      .catch(() => {
        showError(UPLOAD_ANNOTATIONS_ERROR_MESSAGE);
      });
  };

  const handleCancel = () => {
    setPreparedForUpload([]);
    setSelectedProvider(null);
    onClose();
  };

  const isUploadDisabled = !selectedProvider || !preparedForUpload.length;

  return (
    <DefaultModal
      open={open}
      onClose={onClose}
      styles={{
        padding: '0',
        borderRadius: '0.5rem',
        width: '35vw',
        height: '50rem',
      }}
    >
      <Box>
        <Typography className={classes.modalTitle}>
          ATTACH ANNOTATIONS FILES
        </Typography>
        <Divider className={classes.divider} />
        <Box className={classes.modalContentWrapper}>
          <Typography
            fontSize="0.9rem"
            color={TRANSPARENT_BLACK_50}
            marginBottom="1rem"
          >
            Select the file(s) you are uploading:
          </Typography>
          <Grid container gap={0} columnSpacing={2} rowSpacing={0}>
            {ANNOTATION_PROVIDERS.map((provider) => (
              <Grid item key={provider.name}>
                <Button
                  className={getProviderClassName(provider.name)}
                  key={provider.name}
                  onClick={() => setSelectedProvider(provider.name)}
                  startIcon={
                    <img
                      src={provider.icon}
                      className={classes.providerIcon}
                      alt={provider.name}
                    />
                  }
                >
                  {provider.name}
                  <Box className={getBubbleClassName(provider.name)}>
                    <CheckOutlinedIcon
                      fontSize="0.75rem"
                      sx={{ color: WHITE }}
                    />
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
          <AnnotationsDropZone
            classes={classes}
            selectedProvider={selectedProvider}
            onAddToUpload={onAddToUpload}
            isBatch={isBatch}
          />
          <Typography
            fontSize="0.9rem"
            color={TRANSPARENT_BLACK_50}
            margin="1rem 0"
          >
            {preparedForUpload.length ? 'Selected Files:' : ' '}
          </Typography>
        </Box>
        <FilesList
          classes={classes}
          preparedForUpload={preparedForUpload}
          onDeleteFromUpload={onDeleteFromUpload}
        />
        <Box
          display="flex"
          justifyContent="space-between"
          marginTop="1rem"
          padding="0 2rem"
        >
          <Button
            variant="contained"
            onClick={handleCancel}
            className={`${classes.modalButton} ${classes.modalButtonCancel}`}
          >
            Cancel
          </Button>
          <Tooltip
            title={isUploadDisabled ? 'Please select file(s) to upload' : ''}
          >
            <span>
              <Button
                disabled={isUploadDisabled}
                variant="contained"
                className={`${classes.modalButton} ${classes.modalButtonProceed}`}
                onClick={handleUpload}
              >
                Proceed
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </DefaultModal>
  );
};
